import React, { useState, useEffect } from 'react';
import { Plus, X, Search, Filter, CheckCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  GetAllPaymentsAction, 
  AddPaymentAction,
  GetPaymentByIdAction,
  GetPaymentDetailsByIdAction,
  UpdatePaymentAction,
} from "../../actions/PaymentAction";
import { GetAllLicensesAction } from "../../actions/LicenseAction";

// Import selector functions
import {
  selectAllPaymentsData,
  selectAllPaymentsLoading,
  selectAllPaymentsError,
  selectAddPaymentData,
  selectAddPaymentLoading,
  selectAddPaymentError,
  selectPaymentByIdData,
  selectPaymentByIdLoading,
  selectPaymentByIdError,
  selectPaymentDetailsByIdData,
  selectPaymentDetailsByIdLoading,
  selectPaymentDetailsByIdError,
  selectUpdatePaymentData,
  selectUpdatePaymentLoading,
  selectUpdatePaymentError,
} from "../../reducers/PaymentReducer";

// Import Components
import AddPaymentModal from '../../components/modals/AddPaymentModal';
import UpdatePaymentModal from '../../components/modals/UpdatePaymentModal';
import PaymentDetailsModal from '../../components/modals/PaymentDetailsModal';
import PaymentsTable from '../../components/tables/PaymentsTable';
import PaymentPagination from '../../components/common/PaymentPagination';

// Toast Notification Component
const ToastNotification = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000); // Auto close after 4 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' 
    ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
    : 'bg-gradient-to-r from-red-500 to-red-600';
  
  const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-4 md:px-6 py-3 md:py-4 rounded-lg md:rounded-xl shadow-xl z-50 animate-slide-in border-l-4 ${type === 'success' ? 'border-emerald-400' : 'border-red-400'}`}>
      <div className="flex items-center space-x-2 md:space-x-3">
        <div className="relative">
          <div className={`w-6 h-6 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center ${iconColor}`}>
            {type === 'success' ? (
              <CheckCircle size={14} className="text-green-500" />
            ) : (
              <X size={14} className="text-red-500" />
            )}
          </div>
          <div className="absolute inset-0 animate-ping bg-white rounded-full opacity-20"></div>
        </div>
        <div className="flex-1">
          <div className="font-bold text-xs md:text-sm">{type === 'success' ? 'Success!' : 'Error!'}</div>
          <div className="text-xs md:text-sm">{message}</div>
        </div>
        <button 
          onClick={onClose}
          className="ml-1 md:ml-2 text-white hover:text-gray-200 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const PaymentManagement = () => {
  const dispatch = useDispatch();

  // Selector functions
  const paymentsData = useSelector(selectAllPaymentsData);
  const paymentsLoading = useSelector(selectAllPaymentsLoading);
  const paymentsError = useSelector(selectAllPaymentsError);

  const addPaymentResult = useSelector(selectAddPaymentData);
  const addPaymentLoading = useSelector(selectAddPaymentLoading);
  const addPaymentError = useSelector(selectAddPaymentError);

  const paymentByIdData = useSelector(selectPaymentByIdData);
  const paymentByIdLoading = useSelector(selectPaymentByIdLoading);
  const paymentByIdError = useSelector(selectPaymentByIdError);

  const paymentDetailsData = useSelector(selectPaymentDetailsByIdData);
  const paymentDetailsLoading = useSelector(selectPaymentDetailsByIdLoading);
  const paymentDetailsError = useSelector(selectPaymentDetailsByIdError);

  const updatePaymentResult = useSelector(selectUpdatePaymentData);
  const updatePaymentLoading = useSelector(selectUpdatePaymentLoading);
  const updatePaymentError = useSelector(selectUpdatePaymentError);

  const allLicensesState = useSelector((state) => state.alllicenses);
  const licensesData = allLicensesState?.responseBody;

  const [payments, setPayments] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchMode, setSearchMode] = useState('all');
  const [searchNotFound, setSearchNotFound] = useState(false);
  
  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [newPayment, setNewPayment] = useState({
    license_id: '',
    payment_plan: 'monthly',
    total_amount: '',
    paid_amount: '',
    payment_method: 'Cash',
    remarks: ''
  });

  const [updatePayment, setUpdatePayment] = useState({
    payment_id: '',
    license_id: '',
    invoice_no: '',
    payment_plan: '',
    total_amount: '',
    paid_amount: '',
    original_paid_amount: '',
    balance_amount: '',
    payment_method: 'Cash',
    remarks: '',
    status: '',
    created_date: ''
  });

  // Show notification function
  const showSuccessNotification = (message) => {
    setNotificationMessage(message);
    setNotificationType('success');
    setShowNotification(true);
  };

  const showErrorNotification = (message) => {
    setNotificationMessage(message);
    setNotificationType('error');
    setShowNotification(true);
  };

  useEffect(() => {
    loadPayments();
    loadLicenses();
  }, []);

  useEffect(() => {
    if (paymentsData) {
      formatAndSetPayments(paymentsData);
      setSearchMode('all');
      setSearchNotFound(false);
    }
  }, [paymentsData]);

  useEffect(() => {
    if (licensesData) {
      formatAndSetLicenses(licensesData);
    }
  }, [licensesData]);

  useEffect(() => {
    if (paymentByIdData && searchMode === 'single') {
      formatAndSetSinglePayment(paymentByIdData);
      setCurrentPage(1);
    }
  }, [paymentByIdData, searchMode]);

  useEffect(() => {
    if (paymentByIdError && searchMode === 'single') {
      setPayments([]);
      setSearchNotFound(true);
    }
  }, [paymentByIdError, searchMode]);

  useEffect(() => {
    if (addPaymentResult && !addPaymentLoading) {
      const isSuccess = addPaymentResult.success !== false;
      
      if (isSuccess) {
        showSuccessNotification(`Payment added successfully for License ID: ${newPayment.license_id}`);
        setShowAddForm(false);
        setNewPayment({
          license_id: '',
          payment_plan: 'monthly',
          total_amount: '',
          paid_amount: '',
          payment_method: 'Cash',
          remarks: ''
        });
        
        setTimeout(() => {
          loadPayments();
        }, 1000);
      } else {
        showErrorNotification('Failed to add payment. Please try again.');
      }
    }
  }, [addPaymentResult, addPaymentLoading]);

  useEffect(() => {
    if (updatePaymentResult && !updatePaymentLoading) {
      const isSuccess = updatePaymentResult.success !== false;
      
      if (isSuccess) {
        // Show success notification
        showSuccessNotification(`✅ Successfully updated Payment ID #${updatePayment.payment_id}`);
        
        setShowUpdateForm(false);
        setUpdatePayment({
          payment_id: '',
          license_id: '',
          invoice_no: '',
          payment_plan: '',
          total_amount: '',
          paid_amount: '',
          original_paid_amount: '',
          balance_amount: '',
          payment_method: 'Cash',
          remarks: '',
          status: '',
          created_date: ''
        });
        
        setTimeout(() => {
          loadPayments();
        }, 1000);
      } else {
        showErrorNotification('Failed to update payment. Please try again.');
      }
    }
  }, [updatePaymentResult, updatePaymentLoading]);

  useEffect(() => {
    if (paymentByIdData && showDetails) {
      formatAndSetSelectedPayment(paymentByIdData);
    }
  }, [paymentByIdData, showDetails]);

  useEffect(() => {
    if (paymentDetailsData && showDetails) {
      formatAndSetPaymentDetails(paymentDetailsData);
    }
  }, [paymentDetailsData, showDetails]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const formatAndSetPayments = (data) => {
    let paymentsArray = [];

    if (data && typeof data === 'object') {
      if (data.StatusCode === 200 && Array.isArray(data.ResultSet)) {
        paymentsArray = data.ResultSet;
      } 
      else if (Array.isArray(data.ResultSet)) {
        paymentsArray = data.ResultSet;
      }
      else if (Array.isArray(data)) {
        paymentsArray = data;
      }
      else if (Array.isArray(data.data)) {
        paymentsArray = data.data;
      }
      else if (Array.isArray(data.responseBody)) {
        paymentsArray = data.responseBody;
      }
      else if (Array.isArray(data.payments)) {
        paymentsArray = data.payments;
      }
      else if (data.payment_id) {
        paymentsArray = [data];
      }
    }

    if (paymentsArray.length === 0) {
      setPayments([]);
      setSearchNotFound(true);
      return;
    }

    const formatted = paymentsArray.map((payment, index) => {
      const totalAmount = parseFloat(payment.total_amount) || 0;
      const paidAmount = parseFloat(payment.paid_amount) || 0;
      const balanceAmount = parseFloat(payment.balance_amount) || 0;
      
      let statusText = 'Unpaid';
      if (payment.status === 'F') {
        statusText = 'Paid';
      } else if (payment.status === 'P') {
        statusText = 'Partial';
      } else if (payment.status === 'A') {
        statusText = 'Unpaid';
      } else if (payment.status === 'Paid') {
        statusText = 'Paid';
      } else if (payment.status === 'Partial') {
        statusText = 'Partial';
      } else if (payment.status === 'Unpaid') {
        statusText = 'Unpaid';
      }
      
      return {
        payment_id: payment.payment_id || payment.PaymentID || payment.id || `PMT-${index}`,
        license_id: payment.license_id || payment.LicenseID || 'Not specified',
        invoice_no: payment.invoice_no || payment.InvoiceNo || `INV-${Date.now()}-${index}`,
        payment_plan: payment.payment_plan || payment.PaymentPlan || 'monthly',
        total_amount: totalAmount,
        paid_amount: paidAmount,
        balance_amount: balanceAmount,
        status: statusText,
        created_date: payment.created_date || payment.CreatedDate || new Date().toISOString().split('T')[0],
        payment_method: payment.payment_method || payment.PaymentMethod || 'Cash',
        remarks: payment.remarks || payment.Remarks || ''
      };
    });

    setPayments(formatted);
    setSearchNotFound(false);
  };

  const formatAndSetSinglePayment = (data) => {
    let paymentData = null;

    if (data && typeof data === 'object') {
      if (data.StatusCode === 200 && Array.isArray(data.ResultSet) && data.ResultSet.length > 0) {
        paymentData = data.ResultSet[0];
      }
      else if (Array.isArray(data.ResultSet) && data.ResultSet.length > 0) {
        paymentData = data.ResultSet[0];
      }
      else if (Array.isArray(data) && data.length > 0) {
        paymentData = data[0];
      }
      else if (data.payment_id || data.PaymentID) {
        paymentData = data;
      }
      else if (data.ResultSet && typeof data.ResultSet === 'object') {
        paymentData = data.ResultSet;
      }
    }

    if (!paymentData) {
      setPayments([]);
      setSearchNotFound(true);
      return;
    }

    const totalAmount = parseFloat(paymentData.total_amount) || 0;
    const paidAmount = parseFloat(paymentData.paid_amount) || 0;
    const balanceAmount = parseFloat(paymentData.balance_amount) || 0;
    
    let statusText = 'Unpaid';
    if (paymentData.status === 'F') {
      statusText = 'Paid';
    } else if (paymentData.status === 'P') {
      statusText = 'Partial';
    } else if (paymentData.status === 'A') {
      statusText = 'Unpaid';
    }

    const formattedPayment = {
      payment_id: paymentData.payment_id || paymentData.PaymentID || paymentData.id || 'Not available',
      license_id: paymentData.license_id || paymentData.LicenseID || 'Not specified',
      invoice_no: paymentData.invoice_no || paymentData.InvoiceNo || 'Not available',
      payment_plan: paymentData.payment_plan || paymentData.PaymentPlan || 'monthly',
      total_amount: totalAmount,
      paid_amount: paidAmount,
      balance_amount: balanceAmount,
      status: statusText,
      created_date: paymentData.created_date || paymentData.CreatedDate || new Date().toISOString().split('T')[0],
      payment_method: paymentData.payment_method || paymentData.PaymentMethod || 'Cash',
      remarks: paymentData.remarks || paymentData.Remarks || ''
    };

    setPayments([formattedPayment]);
    setSearchNotFound(false);
  };

  const formatAndSetLicenses = (data) => {
    let licensesArray = [];

    if (data && typeof data === 'object') {
      if (data.StatusCode === 200 && Array.isArray(data.ResultSet)) licensesArray = data.ResultSet;
      else if (Array.isArray(data.ResultSet)) licensesArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) licensesArray = data.resultSet;
      else if (Array.isArray(data.data)) licensesArray = data.data;
      else if (Array.isArray(data.responseBody)) licensesArray = data.responseBody;
      else if (Array.isArray(data.licenses)) licensesArray = data.licenses;
      else if (data.license_id) licensesArray = [data];
    }

    const formatted = licensesArray.map((license, index) => ({
      license_id: license.license_id || license.LicenseID || license.id || `temp-${index}`,
      license_key: license.license_key || license.LicenseKey || `KEY-${index}`,
      business_id: license.business_id || license.BusinessID || 'Not specified'
    }));

    setLicenses(formatted);
  };

  const formatAndSetSelectedPayment = (data) => {
    let paymentData = null;
    
    if (data && typeof data === 'object') {
      if (data.StatusCode === 200 && Array.isArray(data.ResultSet) && data.ResultSet.length > 0) {
        paymentData = data.ResultSet[0];
      }
      else if (Array.isArray(data.ResultSet) && data.ResultSet.length > 0) {
        paymentData = data.ResultSet[0];
      }
      else if (Array.isArray(data) && data.length > 0) {
        paymentData = data[0];
      }
      else if (data.payment_id || data.PaymentID) {
        paymentData = data;
      }
      else if (data.ResultSet && typeof data.ResultSet === 'object') {
        paymentData = data.ResultSet;
      }
    }

    if (!paymentData) {
      return;
    }

    const totalAmount = parseFloat(paymentData.total_amount) || 0;
    const paidAmount = parseFloat(paymentData.paid_amount) || 0;
    const balanceAmount = parseFloat(paymentData.balance_amount) || 0;
    
    let statusText = 'Unpaid';
    if (paymentData.status === 'F') {
      statusText = 'Paid';
    } else if (paymentData.status === 'P') {
      statusText = 'Partial';
    } else if (paymentData.status === 'A') {
      statusText = 'Unpaid';
    }

    const payment = {
      payment_id: paymentData.payment_id || paymentData.PaymentID || paymentData.id || 'Not available',
      license_id: paymentData.license_id || paymentData.LicenseID || 'Not specified',
      invoice_no: paymentData.invoice_no || paymentData.InvoiceNo || 'Not available',
      payment_plan: paymentData.payment_plan || paymentData.PaymentPlan || 'monthly',
      total_amount: totalAmount,
      paid_amount: paidAmount,
      balance_amount: balanceAmount,
      status: statusText,
      created_date: paymentData.created_date || paymentData.CreatedDate || new Date().toISOString().split('T')[0],
      payment_method: paymentData.payment_method || paymentData.PaymentMethod || 'Cash',
      remarks: paymentData.remarks || paymentData.Remarks || ''
    };
    
    setSelectedPayment(payment);
  };

  const formatAndSetPaymentDetails = (data) => {
    let detailsArray = [];

    if (data && typeof data === 'object') {
      if (data.StatusCode === 200 && Array.isArray(data.ResultSet)) detailsArray = data.ResultSet;
      else if (Array.isArray(data.ResultSet)) detailsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) detailsArray = data.resultSet;
      else if (Array.isArray(data.data)) detailsArray = data.data;
      else if (Array.isArray(data.responseBody)) detailsArray = data.responseBody;
      else if (Array.isArray(data.paymentDetails)) detailsArray = data.paymentDetails;
      else if (data.payment_date) detailsArray = [data];
    }

    const formatted = detailsArray.map((detail, index) => ({
      payment_date: detail.payment_date || detail.PaymentDate || new Date().toISOString().split('T')[0],
      payment_method: detail.payment_method || detail.PaymentMethod || 'Cash',
      reference_no: detail.reference_no || detail.ReferenceNo || `REF-${Date.now()}-${index}`,
      amount: parseFloat(detail.amount || detail.Amount || '0.00'),
      remarks: detail.remarks || detail.Remarks || 'No remarks'
    }));

    setPaymentDetails(formatted);
  };

  const loadPayments = async () => {
    try {
      setSearchNotFound(false);
      await dispatch(GetAllPaymentsAction());
      setSearchMode('all');
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading payments:', error);
      showErrorNotification('Failed to load payments. Please try again.');
    }
  };

  const loadLicenses = async () => {
    try {
      await dispatch(GetAllLicensesAction());
    } catch (error) {
      console.error('Error loading licenses:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchTerm.trim()) {
      await loadPayments();
      return;
    }

    try {
      setSearchNotFound(false);
      
      if (!isNaN(searchTerm)) {
        setSearchMode('single');
        await dispatch(GetPaymentByIdAction(searchTerm));
      } else {
        const filtered = payments.filter(payment => 
          payment.payment_id.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
          payment.invoice_no.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
          payment.license_id.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        if (filtered.length === 0) {
          setPayments([]);
          setSearchNotFound(true);
          showErrorNotification(`No payments found for "${searchTerm}"`);
        } else {
          setPayments(filtered);
          setSearchMode('all');
          showSuccessNotification(`Found ${filtered.length} payment(s) matching "${searchTerm}"`);
        }
      }
    } catch (error) {
      console.error('Error searching payment:', error);
      setPayments([]);
      setSearchNotFound(true);
      showErrorNotification('Search failed. Please try again.');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();

    if (!newPayment.license_id || !newPayment.total_amount) {
      showErrorNotification('License and Total Amount are required');
      return;
    }

    try {
      const createData = {
        license_id: newPayment.license_id,
        payment_plan: newPayment.payment_plan,
        total_amount: newPayment.total_amount,
        paid_amount: newPayment.paid_amount || 0,
        payment_method: newPayment.payment_method,
        remarks: newPayment.remarks
      };
      
      await dispatch(AddPaymentAction(createData));
    } catch (error) {
      console.error('Error adding payment:', error);
      showErrorNotification('Failed to add payment. Please try again.');
    }
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();

    if (!updatePayment.payment_id) {
      showErrorNotification('Payment ID is required');
      return;
    }

    try {
      const totalAmount = parseFloat(updatePayment.total_amount) || 0;
      const currentPaidAmount = parseFloat(updatePayment.original_paid_amount || updatePayment.paid_amount) || 0;
      const newPaidAmountInput = updatePayment.paid_amount === '' ? '' : updatePayment.paid_amount;
      let newPaidAmount;
      
      if (newPaidAmountInput === '') {
        newPaidAmount = currentPaidAmount;
      } else {
        newPaidAmount = parseFloat(newPaidAmountInput) || 0;
      }
      
      const newBalanceAmount = totalAmount - newPaidAmount;
      
      if (newPaidAmount > totalAmount) {
        showErrorNotification('Paid amount cannot exceed total amount');
        return;
      }

      if (newPaidAmount < 0) {
        showErrorNotification('Paid amount cannot be negative');
        return;
      }

      const updateData = {
        payment_id: updatePayment.payment_id,
        license_id: updatePayment.license_id,
        total_amount: totalAmount,
        paid_amount: newPaidAmount,
        payment_method: updatePayment.payment_method || 'Cash',
        remarks: updatePayment.remarks || ''
      };

      await dispatch(UpdatePaymentAction(updateData));
    } catch (error) {
      console.error('Error updating payment:', error);
      showErrorNotification('Error updating payment. Please try again.');
    }
  };

  const handleViewDetails = async (payment) => {
    try {
      setShowDetails(true);
      setSelectedPayment(payment);
      
      if (payment.payment_id) {
        await dispatch(GetPaymentByIdAction(payment.payment_id));
        await dispatch(GetPaymentDetailsByIdAction(payment.payment_id));
      }
      
    } catch (error) {
      console.error('Error loading payment details:', error);
      setSelectedPayment(payment);
    }
  };

  const handleEditClick = (payment) => {
  // Prevent editing paid payments
  if (payment.status === 'Paid') {
    showErrorNotification('Cannot edit paid payments. Payment is already completed.');
    return;
  }
  
  const currentBalance = (parseFloat(payment.total_amount) || 0) - (parseFloat(payment.paid_amount) || 0);
  
  setUpdatePayment({
    payment_id: payment.payment_id,
    license_id: payment.license_id,
    invoice_no: payment.invoice_no,
    payment_plan: payment.payment_plan || 'monthly',
    total_amount: payment.total_amount || 0,
    paid_amount: '', // Empty initially for the input field
    original_paid_amount: payment.paid_amount || 0, // Store original paid amount
    balance_amount: currentBalance,
    payment_method: payment.payment_method || 'Cash',
    remarks: payment.remarks || '',
    status: payment.status || 'Unpaid',
    created_date: payment.created_date || ''
  });
  setShowUpdateForm(true);
};

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setNewPayment({
      license_id: '',
      payment_plan: 'monthly',
      total_amount: '',
      paid_amount: '',
      payment_method: 'Cash',
      remarks: ''
    });
  };

  const handleCloseUpdateForm = () => {
    setShowUpdateForm(false);
    setUpdatePayment({
      payment_id: '',
      license_id: '',
      invoice_no: '',
      payment_plan: '',
      total_amount: '',
      paid_amount: '',
      original_paid_amount: '',
      balance_amount: '',
      payment_method: 'Cash',
      remarks: '',
      status: '',
      created_date: ''
    });
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedPayment(null);
    setPaymentDetails([]);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchMode('all');
    setSearchNotFound(false);
    loadPayments();
    showSuccessNotification('Search cleared. Showing all payments.');
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'Partial':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'Unpaid':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getPaymentPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'monthly':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'yearly':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      case 'onetime':
        return 'bg-green-100 text-green-800 border border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // Get current payments for the current page
  const getCurrentPayments = () => {
    // Filter by status first
    const filteredByStatus = payments.filter(p => 
      filterStatus === 'all' || p.status === filterStatus
    );
    
    // Then apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    return filteredByStatus.slice(startIndex, endIndex);
  };

  // Calculate total pages for filtered payments
  const getTotalFilteredPages = () => {
    const filteredByStatus = payments.filter(p => 
      filterStatus === 'all' || p.status === filterStatus
    );
    return Math.ceil(filteredByStatus.length / itemsPerPage);
  };

  // Get total filtered items count
  const getTotalFilteredItems = () => {
    return payments.filter(p => 
      filterStatus === 'all' || p.status === filterStatus
    ).length;
  };

  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      {/* Add CSS animation styles */}
      <style>
        {`
          @keyframes slide-in {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .animate-slide-in {
            animation: slide-in 0.3s ease-out;
          }
          @keyframes ping {
            75%, 100% {
              transform: scale(2);
              opacity: 0;
            }
          }
          .animate-ping {
            animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
          }
        `}
      </style>

      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Payment Management</h1>
        <p className="text-sm md:text-base text-gray-600">Manage all payments and transactions</p>
      </div>

      {(paymentsError || addPaymentError || paymentByIdError || paymentDetailsError || 
        updatePaymentError) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {paymentsError || addPaymentError || paymentByIdError || paymentDetailsError || 
            updatePaymentError}
        </div>
      )}

<div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
  <div className="flex-1 max-w-full lg:max-w-md relative w-full">
    <form onSubmit={handleSearch} className="flex items-center space-x-2">
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search by Payment ID (number), Invoice No, or License ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 md:pl-10 pr-8 md:pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          disabled={paymentsLoading || paymentByIdLoading}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </form>
  </div>

  {/* View Mode and Add Button */}
  <div className="flex items-center space-x-2 md:space-x-4 w-full lg:w-auto">
    <div className="flex items-center space-x-2">
      <Filter size={18} className="text-gray-400 hidden md:block" />
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-full md:w-auto"
        disabled={paymentsLoading || paymentByIdLoading}
      >
        <option value="all">All Status</option>
        <option value="Paid">Paid</option>
        <option value="Partial">Partial</option>
        <option value="Unpaid">Unpaid</option>
      </select>
    </div>
    
    <button
      onClick={() => setShowAddForm(true)}
      disabled={addPaymentLoading || paymentsLoading || paymentByIdLoading}
      className="bg-blue-500 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2 font-medium disabled:bg-blue-300 disabled:cursor-not-allowed w-full md:w-auto"
    >
      <Plus size={18} />
      <span className="text-sm md:text-base">{addPaymentLoading ? 'Adding...' : 'Add Payment'}</span>
    </button>
  </div>
</div>

      {/* Modals */}
      {showAddForm && (
        <AddPaymentModal
          showAddForm={showAddForm}
          newPayment={newPayment}
          setNewPayment={setNewPayment}
          licenses={licenses}
          addPaymentLoading={addPaymentLoading}
          addPaymentError={addPaymentError}
          handleAddPayment={handleAddPayment}
          handleCloseAddForm={handleCloseAddForm}
        />
      )}

      {showUpdateForm && (
        <UpdatePaymentModal
          updatePayment={updatePayment}
          setUpdatePayment={setUpdatePayment}
          updatePaymentLoading={updatePaymentLoading}
          handleUpdatePayment={handleUpdatePayment}
          handleCloseUpdateForm={handleCloseUpdateForm}
        />
      )}

      {showDetails && (
        <PaymentDetailsModal
          selectedPayment={selectedPayment}
          paymentDetails={paymentDetails}
          paymentByIdLoading={paymentByIdLoading}
          paymentDetailsLoading={paymentDetailsLoading}
          paymentByIdError={paymentByIdError}
          paymentDetailsError={paymentDetailsError}
          handleCloseDetails={handleCloseDetails}
          getPaymentPlanBadgeColor={getPaymentPlanBadgeColor}
          getStatusBadgeColor={getStatusBadgeColor}
        />
      )}

      {/* Notification */}
      {showNotification && (
        <ToastNotification
          message={notificationMessage}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/* Loading State */}
      {(paymentsLoading || paymentByIdLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600 text-sm md:text-base">
            {paymentByIdLoading ? 'Searching payments...' : 'Loading payments...'}
          </p>
        </div>
      )}

      {/* Payments Table Container */}
      {!paymentsLoading && !paymentByIdLoading && (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="overflow-x-auto">
            <PaymentsTable
              payments={getCurrentPayments()}
              filterStatus={filterStatus}
              searchNotFound={searchNotFound}
              searchTerm={searchTerm}
              paymentsLoading={paymentsLoading}
              paymentByIdLoading={paymentByIdLoading}
              handleViewDetails={handleViewDetails}
              handleEditClick={handleEditClick}
              handleClearSearch={handleClearSearch}
              getStatusBadgeColor={getStatusBadgeColor}
            />
          </div>
          
          {/* Add PaymentPagination here */}
          {payments.length > 0 && (
            <div className="mt-4">
              <PaymentPagination
                currentPage={currentPage}
                totalPages={getTotalFilteredPages()}
                totalItems={getTotalFilteredItems()}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                disabled={paymentsLoading || paymentByIdLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;